import React, { useContext, useEffect, useMemo, useRef, useState } from "react";

import {
    Add,
    Cancel,
    CheckCircleOutline,
    CompareArrows,
    PersonAddAlt,
    Print,
    Receipt,
} from "@mui/icons-material";
import { ListItemIcon } from "@mui/material";
import { Box, CircularProgress, Typography } from "@mui/material";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import axios from "axios";
import { enqueueSnackbar } from "notistack";
import SwitchTableAlert from "../../../components/SwitchTableAlert";
import { PosContext } from "../../../shared/PosContext";
import {
    useCancelReservationData,
} from "../../../shared/hooks/useTableOrdersData";
import { usePrintOrder } from "../../../shared/hooks/useTablesData";
import MoveOrdersDialog from "../../Widgets/MoveOrdersDialog";
import DragableTable from "./DragableTable";
import PrintOrderModal from "./PrintOrderModal";
import { useStyles } from "./styles";
import { AppContext } from "../../../AppContext";
import { isRoleService } from "../../../frappeConfig";
import { useUpdateDoctypeData } from "../../../shared/hooks/useDocTypesData";
import { getCurrentDate } from "../../../utils/dateUtils";

// Cache floor plan metadata across mounts so navigating POS -> Tables doesn't "white screen"
// while the same room image reloads.
const floorPlanCache = new Map(); // roomName -> { url: string, originalWidth: number, originalHeight: number }

/**
 * Compute the display dimensions for the floor-plan image so it fits the
 * current viewport.  Must be called AFTER the browser has finished layout
 * (i.e. inside a requestAnimationFrame callback) so that Pos_Header has its
 * final clientHeight (logo may still be loading on first paint otherwise).
 */
const computeScaled = (origWidth, origHeight) => {
    if (!origWidth || !origHeight) return { w: 0, h: 0 };

    const bodyWidth = document.body.clientWidth;
    if (origWidth !== bodyWidth) {
        let newWidth = bodyWidth * 0.98;
        let newHeight = (origHeight * newWidth) / origWidth;

        const posHeaderElement = document.getElementById("Pos_Header");
        const headerHeight = posHeaderElement ? posHeaderElement.clientHeight : 0;
        const maxHeight = window.innerHeight - headerHeight;

        if (maxHeight > 0 && maxHeight < newHeight) {
            newHeight = maxHeight;
            newWidth = (origWidth * newHeight) / origHeight;
        }

        return { w: newWidth, h: newHeight };
    }

    return { w: origWidth, h: origHeight };
};

const Room = ({
    room,
    onTableSelect,
    moving,
    handleOnChangeMove,
    handleCloseTableExt,
}) => {
    const classes = useStyles();

    const {
        tables,
        selectedTable,
        setSelectedTable,
        refetchTablesData,
        discountEnabled,
    } = useContext(PosContext);

    const {
        restaurantSettings,
        userInfo,
        foodLateTimeEnabled,
        setLoadingTable,
    } = useContext(AppContext);

    const [selectable, setSelectable] = useState(false);
    const [backgroundUrl, setBackgroundUrl] = useState("");
    const [imageWidth, setImageWidth] = useState(0);
    const [imageHeight, setImageHeight] = useState(0);

    const [oreginalImageWidth, setOreginalImageWidth] = useState(0);
    const [oreginalImageHeight, setOreginalImageHeight] = useState(0);
    const [isFloorPlanLoading, setIsFloorPlanLoading] = useState(false);

    // Tracks the original image dimensions so the resize / ResizeObserver
    // handlers can recompute scaled dimensions without needing state reads.
    const originalDimsRef = useRef({ width: 0, height: 0 });

    const [movingOrdersOpen, setMovingOrdersOpen] = useState(false);
    const printOrder = usePrintOrder();
    const markFoodReadyMutation = useUpdateDoctypeData();
    const cancelReservationMutation = useCancelReservationData();

    const [printOrderModal, setPrintOrderModal] = useState(false);
    const togglePrintOrder = () => setPrintOrderModal(!printOrderModal);

    /**
     * Apply scaled dimensions derived from original image dimensions.
     * Must be called inside a requestAnimationFrame so the DOM (especially
     * Pos_Header which contains the logo image) is fully laid out before we
     * measure heights.
     */
    const applyScaledDimensions = (origWidth, origHeight, url) => {
        originalDimsRef.current = { width: origWidth, height: origHeight };
        requestAnimationFrame(() => {
            const { w, h } = computeScaled(origWidth, origHeight);
            setOreginalImageWidth(origWidth);
            setOreginalImageHeight(origHeight);
            setImageWidth(w);
            setImageHeight(h);
            if (url !== undefined) {
                setBackgroundUrl(url);
            }
            setIsFloorPlanLoading(false);
        });
    };

    useEffect(() => {
        const roomName = room?.name;
        if (!roomName) return;

        // Fast path: use cached plan immediately (no blank UI).
        // We still defer the dimension calculation to a rAF so that
        // Pos_Header has its final height (logo may not be loaded yet
        // on the very first render on slow devices).
        const cached = floorPlanCache.get(roomName);
        if (cached?.url && cached?.originalWidth && cached?.originalHeight) {
            applyScaledDimensions(
                cached.originalWidth,
                cached.originalHeight,
                cached.url
            );
            return;
        }

        setIsFloorPlanLoading(true);

        if (room) {
            const isLocalhost =
                window.location.hostname === "localhost" ||
                window.location.hostname === "127.0.0.1";

            const params = {
                doctype: "Restaurant Object",
                name: roomName,
            };

            if (params.name) {
                axios
                    .post(
                        "/method/frappe.desk.form.load.getdoc",
                        JSON.stringify(params)
                    )
                    .then((res) => {
                        const floorPlanUrl =
                            res.data.docs?.[0]?.floor_plan || "";

                        if (!floorPlanUrl) {
                            console.warn("No floor plan URL found in docs");
                            return;
                        }

                        const image = new Image();
                        // When running on localhost, S3 may not allow the dev origin in its CORS rules.
                        // Setting crossOrigin="anonymous" would hard-block the image load in that case.
                        if (!isLocalhost) {
                            image.crossOrigin = "anonymous";
                        }

                        image.onload = () => {
                            const width = image.width;
                            const height = image.height;
                            const encoded = encodeURI(floorPlanUrl);
                            // Cache before the rAF so a fast unmount+remount still benefits.
                            floorPlanCache.set(roomName, {
                                url: encoded,
                                originalWidth: width,
                                originalHeight: height,
                            });
                            // Defer DOM measurements to after paint so Pos_Header
                            // has its final height.
                            applyScaledDimensions(width, height, encoded);
                        };

                        image.onerror = () => {
                            // Fallback to attachment URL if direct floor_plan fails
                            const attachmentUrl =
                                res.data.docinfo.attachments?.[0]?.file_url ||
                                "";
                            if (
                                attachmentUrl &&
                                attachmentUrl !== floorPlanUrl
                            ) {
                                console.log("Fallback URL:", attachmentUrl);
                                const fallbackImage = new Image();
                                if (!isLocalhost) {
                                    fallbackImage.crossOrigin = "anonymous";
                                }

                                fallbackImage.onload = () => {
                                    const width = fallbackImage.width;
                                    const height = fallbackImage.height;
                                    console.log(
                                        "Fallback image dimensions:",
                                        width,
                                        height
                                    );
                                    const encoded = encodeURI(attachmentUrl);
                                    floorPlanCache.set(roomName, {
                                        url: encoded,
                                        originalWidth: width,
                                        originalHeight: height,
                                    });
                                    applyScaledDimensions(width, height, encoded);
                                };

                                fallbackImage.onerror = () => {
                                    console.error(
                                        "Both floor plan and attachment URLs failed to load"
                                    );
                                    setIsFloorPlanLoading(false);
                                };

                                fallbackImage.src = attachmentUrl;
                            }
                        };

                        image.src = floorPlanUrl;
                    })
                    .catch((error) => {
                        console.error("Error fetching room data:", error);
                        setIsFloorPlanLoading(false);
                    });
            }
        }
    }, [room?.name]);

    // Recompute floor-plan dimensions whenever:
    //  • the browser window is resized, OR
    //  • Pos_Header changes height (e.g. logo finishes loading, font swaps)
    // This is the primary guard against the "wrong position on first load"
    // bug on slow devices where the header hasn't settled by the time the
    // initial computeScaled call fires.
    useEffect(() => {
        let rafId = null;

        const recompute = () => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                const { width, height } = originalDimsRef.current;
                if (width && height) {
                    const { w, h } = computeScaled(width, height);
                    setImageWidth(w);
                    setImageHeight(h);
                }
            });
        };

        window.addEventListener("resize", recompute);

        // Watch the header so we recompute if the logo changes its height.
        let headerObserver = null;
        const posHeader = document.getElementById("Pos_Header");
        if (posHeader && typeof ResizeObserver !== "undefined") {
            headerObserver = new ResizeObserver(recompute);
            headerObserver.observe(posHeader);
        }

        return () => {
            window.removeEventListener("resize", recompute);
            if (headerObserver) headerObserver.disconnect();
            if (rafId) cancelAnimationFrame(rafId);
        };
    }, []);

    const tableClick = (table) => {
        if (onTableSelect) onTableSelect(table);
    };

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleOpenMenu = (event, table) => {
        setAnchorEl(event.currentTarget);
        setSelectedTable(table);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setSelectedTable(null);
    };

    const handleCloseTable = () => {
        setAnchorEl(null);
        if (selectedTable) {
            if (handleCloseTableExt) {
                handleCloseTableExt(selectedTable);
            }
        }
    };

    const handleOrder = () => {
        setAnchorEl(null);

        if (selectedTable) {
            tableClick(selectedTable);
        }
    };

    const handleReserve = () => {
        setAnchorEl(null);

        if (selectedTable) {
            const deleteEvent = new CustomEvent("reserveTable", {
                detail: { table: selectedTable },
            });
            document.dispatchEvent(deleteEvent);
        }
    };

    const handleCancelReservation = () => {
        if (!selectedTable?.name || cancelReservationMutation.isLoading) {
            return;
        }
        cancelReservationMutation.mutate(
            { tableName: selectedTable.name },
            {
                onSuccess: (res) => {
                    const payload = res?.message;
                    const msg =
                        (payload &&
                            typeof payload === "object" &&
                            payload.message) ||
                        (typeof payload === "string" ? payload : null) ||
                        "Reservation cancelled";
                    enqueueSnackbar(msg, { variant: "success" });
                    refetchTablesData();
                    setAnchorEl(null);
                    setSelectedTable(null);
                },
                onError: (error) => {
                    let msg = "Failed to cancel reservation";
                    try {
                        const serverMessages =
                            error?.response?.data?._server_messages;
                        if (serverMessages) {
                            const parsed = JSON.parse(serverMessages);
                            if (parsed.length > 0) {
                                msg =
                                    JSON.parse(parsed[0]).message || msg;
                            }
                        } else if (error?.response?.data?.message) {
                            msg = error.response.data.message;
                        }
                    } catch (_) {
                        // keep default
                    }
                    enqueueSnackbar(msg, { variant: "error" });
                },
            }
        );
    };

    const handleStartMove = () => {
        setAnchorEl(null);

        if (selectedTable) {
            if (handleOnChangeMove) handleOnChangeMove(true);
        }
    };

    const handleMoveOrders = () => {
        setAnchorEl(null);

        if (selectedTable) {
            setSelectable(true);
        }
    };

    const handleMoveOrdersFromTakeAway = (data) => {
        setAnchorEl(null);

        if (selectedTable) {
            setSelectable(true);
        }
    };

    useEffect(() => {
        document.addEventListener(
            "requestMoveToTable",
            handleMoveOrdersFromTakeAway
        );
        return () => {
            document.removeEventListener(
                "requestMoveToTable",
                handleMoveOrdersFromTakeAway
            );
        };
    }, []);

    const onConfirmCancelOrderMove = () => {
        setSelectable(false);
    };

    const [targetTable, setTargetTable] = useState(null);
    const handleTargetTableClicked = (table) => {
        if (table.name === selectedTable.name) {
            //todo warning modal
            alert("Please select a different table.");
        } else {
            setTargetTable(table);
            setMovingOrdersOpen(true);
        }
    };

    const pendingFoodReadyOrder = useMemo(() => {
        if (!selectedTable?.orders) {
            return null;
        }
        const orders = Object.values(selectedTable.orders);
        return orders.find((order) => order && !order.food_ready_time) || null;
    }, [selectedTable]);

    const canShowFoodReadyAction =
        foodLateTimeEnabled && !!pendingFoodReadyOrder;

    const handleMarkFoodReadyFromMenu = () => {
        if (!pendingFoodReadyOrder || markFoodReadyMutation.isLoading) {
            return;
        }
        setAnchorEl(null);
        setLoadingTable(selectedTable?.name || "");
        markFoodReadyMutation.mutate(
            {
                doctype: "Table Order",
                name: pendingFoodReadyOrder.name,
                formData: JSON.stringify({
                    food_ready_time: getCurrentDate(),
                }),
            },
            {
                onSuccess: () => {
                    refetchTablesData();
                    enqueueSnackbar("Order marked ready", {
                        variant: "success",
                    });
                    setAnchorEl(null);
                },
                onError: (error) => {
                    console.error("Failed to mark food ready", error);
                    enqueueSnackbar("Unable to mark order as ready", {
                        variant: "error",
                    });
                },
                onSettled: () => {
                    setLoadingTable("");
                },
            }
        );
    };

    const isSelectedOrderPrinted = useMemo(() => {
        if (selectedTable && selectedTable.orders) {
            return Object.values(selectedTable.orders).some(
                (order) => order.invoice_printed == 1
            );
        }
        return false;
    }, [selectedTable]);

    return (
        <>
            <div
                style={{
                    backgroundImage: backgroundUrl
                        ? `url("${backgroundUrl}")`
                        : "none",
                    width: imageWidth,
                    height: imageHeight,
                    backgroundSize: "cover",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center",
                    marginTop: "5px",
                    marginLeft: "5px",
                }}
            >
                {isFloorPlanLoading && !backgroundUrl ? (
                    <Box
                        sx={{
                            height: "100%",
                            minHeight: 240,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexDirection: "column",
                            gap: 1,
                            background: "rgba(255,255,255,0.6)",
                        }}
                    >
                        <CircularProgress size={28} />
                        <Typography variant="body2" color="text.secondary">
                            Loading floor plan…
                        </Typography>
                    </Box>
                ) : null}
                {selectable && (
                    <SwitchTableAlert onConfirm={onConfirmCancelOrderMove} />
                )}
                {backgroundUrl &&
                    tables &&
                    tables.map((table, index) => (
                        <DragableTable
                            key={table.name}
                            table={table}
                            handleOpenMenu={handleOpenMenu}
                            open={open}
                            handleStartMoveClicked={handleStartMove}
                            moving={moving}
                            selectable={selectable}
                            imageWidth={oreginalImageWidth}
                            imageHeight={oreginalImageHeight}
                            calculatedImageWidth={imageWidth}
                            calculatedImageHeight={imageHeight}
                            onTargetTableClicked={handleTargetTableClicked}
                        ></DragableTable>
                    ))}

                {!moving && (
                    <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleClose}
                        anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "center",
                        }}
                        transformOrigin={{
                            vertical: "top",
                            horizontal: "center",
                        }}
                    >
                        <MenuItem
                            className={classes.MenuItem}
                            onClick={() => {
                                handleOrder();
                                setAnchorEl(null);
                            }}
                        >
                            <ListItemIcon>
                                <Add />
                            </ListItemIcon>
                            Order
                        </MenuItem>
                        {!isRoleService(userInfo.role_profile_name)
                            ? selectedTable &&
                              selectedTable.numberOfOrders === 0 &&
                              (selectedTable.reserved ? (
                                  <MenuItem
                                      className={classes.MenuItem}
                                      onClick={() => {
                                          handleReserve();
                                          setAnchorEl(null);
                                      }}
                                  >
                                      View/Update reservation
                                  </MenuItem>
                              ) : (
                                  <MenuItem
                                      className={classes.MenuItem}
                                      key="2"
                                      onClick={() => {
                                          handleReserve();
                                          setAnchorEl(null);
                                      }}
                                  >
                                      <ListItemIcon>
                                          <PersonAddAlt />
                                      </ListItemIcon>
                                      Reserve table
                                  </MenuItem>
                              ))
                            : null}
                        {!isRoleService(userInfo.role_profile_name) &&
                        selectedTable &&
                        selectedTable.reserved ? (
                            <MenuItem
                                className={classes.MenuItem}
                                onClick={handleCancelReservation}
                                disabled={cancelReservationMutation.isLoading}
                            >
                                <ListItemIcon>
                                    <Cancel />
                                </ListItemIcon>
                                Cancel reservation
                            </MenuItem>
                        ) : null}
                        {selectedTable && selectedTable.numberOfOrders > 0 ? (
                            <MenuItem
                                className={classes.MenuItem}
                                onClick={() => {
                                    handleMoveOrders();
                                    setAnchorEl(null);
                                }}
                            >
                                <ListItemIcon>
                                    <CompareArrows />
                                </ListItemIcon>
                                Move orders
                            </MenuItem>
                        ) : null}
                        {selectedTable &&
                        selectedTable.numberOfOrders > 0 &&
                        canShowFoodReadyAction ? (
                            <MenuItem
                                className={classes.MenuItem}
                                onClick={handleMarkFoodReadyFromMenu}
                                disabled={markFoodReadyMutation.isLoading}
                            >
                                <ListItemIcon>
                                    <CheckCircleOutline />
                                </ListItemIcon>
                                Ready
                            </MenuItem>
                        ) : null}
                        {!isRoleService(userInfo.role_profile_name) &&
                        selectedTable &&
                        selectedTable.numberOfOrders > 0 &&
                        !selectedTable.reserved ? (
                            <MenuItem
                                className={classes.MenuItem}
                                disabled={
                                    restaurantSettings?.docs[0]
                                        ?.print_order_to_pay == 1
                                        ? !isSelectedOrderPrinted
                                        : false
                                }
                                onClick={() => {
                                    handleCloseTable();
                                    setAnchorEl(null);
                                }}
                            >
                                <ListItemIcon>
                                    <Receipt />
                                </ListItemIcon>
                                close table
                            </MenuItem>
                        ) : null}
                        {!isRoleService(userInfo.role_profile_name) && discountEnabled &&
                        selectedTable &&
                        selectedTable.numberOfOrders > 0 ? (
                            <MenuItem
                                className={classes.MenuItem}
                                onClick={() => {
                                    togglePrintOrder();
                                    setAnchorEl(null);
                                }}
                            >
                                {/* <MenuItem onClick={handlePrintOrders}> */}
                                <ListItemIcon>
                                    <Print />
                                </ListItemIcon>
                                Print Order
                            </MenuItem>
                        ) : null}
                    </Menu>
                )}
            </div>
            <MoveOrdersDialog
                isOpen={movingOrdersOpen}
                targetTable={targetTable}
                selectedTable={selectedTable}
                onClose={() => {
                    setSelectable(false);
                    setTargetTable(null);
                }}
            />
            <PrintOrderModal
                open={printOrderModal}
                onClose={togglePrintOrder}
                orders={selectedTable?.orders}
            />
        </>
    );
};

export default Room;
